import React, { useEffect, useRef, useState, useCallback } from "react";
import { createVoiceCallSignaling } from "../utils/voiceCallSignaling";
import { connectSocket, SERVER_URL } from "../socket";
import useContacts from "../hooks/useContacts";

export default function VoiceCall({
  localUser,
  remoteUser,
  onEnd,
  isCaller,
  incomingOffer,
  contacts: externalContacts = []
}) {
  const { contacts: fetchedContacts } = useContacts();
  const contacts = externalContacts.length > 0 ? externalContacts : fetchedContacts;

  // ── Refs ──────────────────────────────────────────────────────────────────
  const pcRef             = useRef(null);
  const localStreamRef    = useRef(null);
  const remoteAudioRef    = useRef(null);
  const timerRef          = useRef(null);
  const pendingCandidates = useRef([]);
  const isMutedRef        = useRef(false);
  const audioCtxRef       = useRef(null);
  const isEndedRef        = useRef(false);
  const endCallOnceRef    = useRef(null);
  const peersRef          = useRef({});   // conference peers: { email: RTCPeerConnection }

  // ── State ─────────────────────────────────────────────────────────────────
  const [duration,                  setDuration]                  = useState(0);
  const [isMuted,                   setIsMuted]                   = useState(false);
  const [error,                     setError]                     = useState("");
  const [participants,              setParticipants]              = useState([remoteUser]);
  const [showInviteModal,           setShowInviteModal]           = useState(false);
  const [selectedContactsToInvite,  setSelectedContactsToInvite]  = useState([]);

  // ── endCallOnce ───────────────────────────────────────────────────────────
  const endCallOnce = useCallback(() => {
    if (isEndedRef.current) return;
    isEndedRef.current = true;

    // Close all conference peer connections
    Object.values(peersRef.current).forEach(peer => {
      try { peer.close(); } catch {}
    });
    peersRef.current = {};

    // Stop remote audio
    const audio = remoteAudioRef.current;
    if (audio) {
      audio.srcObject = null;
      try { audio.pause(); } catch {}
    }

    // Stop timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Close main peer connection
    const pc = pcRef.current;
    if (pc) {
      pc.ontrack                    = null;
      pc.onicecandidate             = null;
      pc.onconnectionstatechange    = null;
      pc.oniceconnectionstatechange = null;
      pc.close();
      pcRef.current = null;
    }

    // Stop local mic
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }

    // Close AudioContext if used
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch {}
      audioCtxRef.current = null;
    }

    pendingCandidates.current = [];
    isMutedRef.current        = false;

    if (onEnd) onEnd();
  }, [onEnd]);

  // Always keep ref in sync so old closures call the latest version
  useEffect(() => {
    endCallOnceRef.current = endCallOnce;
  }, [endCallOnce]);

  // ── Mute toggle ───────────────────────────────────────────────────────────
  function toggleMute() {
    const newMuted = !isMutedRef.current;
    isMutedRef.current = newMuted;
    setIsMuted(newMuted);

    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(track => {
        track.enabled = !newMuted; // true = unmuted, false = muted
      });
    }
  }

  // ── Attach remote stream to <audio> element ───────────────────────────────
  const attachRemoteStream = useCallback((stream) => {
    const audio = remoteAudioRef.current;
    if (!audio) {
      console.warn("⚠️ remoteAudioRef not ready, retrying in 200ms");
      setTimeout(() => attachRemoteStream(stream), 200);
      return;
    }

    audio.srcObject = stream;
    audio.muted     = false;
    audio.volume    = 1;

    if (!timerRef.current) {
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    }

    setTimeout(() => {
      audio.play().catch(err => {
        if (err.name === "NotAllowedError") {
          console.warn("💡 Autoplay blocked — will play on next user interaction");
        } else {
          console.error("❌ Audio play error:", err.name, err.message);
        }
      });
    }, 150);
  }, []);

  // ── Create a real WebRTC peer for a conference participant ─────────────────
  const createPeerForContact = useCallback(async (email) => {
    if (peersRef.current[email]) return; // already connected

    const pc = new RTCPeerConnection({
      iceServers: [
        {
          urls: "stun:stun.relay.metered.ca:80",
        },
        {
          urls: "turn:global.relay.metered.ca:80",
          username: "a4e8f95f2328fa27921e2991",
          credential: "lT4ZU1kAeW1Vn6Nt",
        },
        {
          urls: "turn:global.relay.metered.ca:80?transport=tcp",
          username: "a4e8f95f2328fa27921e2991",
          credential: "lT4ZU1kAeW1Vn6Nt",
        },
        {
          urls: "turn:global.relay.metered.ca:443",
          username: "a4e8f95f2328fa27921e2991",
          credential: "lT4ZU1kAeW1Vn6Nt",
        },
        {
          urls: "turns:global.relay.metered.ca:443?transport=tcp",
          username: "a4e8f95f2328fa27921e2991",
          credential: "lT4ZU1kAeW1Vn6Nt",
        },
      ],
    });

    peersRef.current[email] = pc;

    // Share local mic with this participant
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t =>
        pc.addTrack(t, localStreamRef.current)
      );
    }

    // Play their audio when it arrives
    pc.ontrack = (event) => {
      const stream = new MediaStream();
      stream.addTrack(event.track);
      const audio = new Audio();
      audio.srcObject = stream;
      audio.autoplay  = true;
      audio.play().catch(console.warn);
    };

    // Send ICE candidates for this peer
    pc.onicecandidate = (e) => {
      if (e.candidate) {
        connectSocket().emit("call_ice", { to: email, candidate: e.candidate });
      }
    };

    // Create offer and send
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    connectSocket().emit("call_offer", { to: email, offer, type: "voice" });

  }, []);

  // ── Invite participants (sends invite + opens real WebRTC connections) ─────
  const inviteToCall = useCallback(async (emailsToInvite) => {
    const socket = connectSocket();

    for (const email of emailsToInvite) {
      socket.emit("conference_invite", {
        to:           email,
        from:         localUser,
        participants: [localUser, remoteUser, ...emailsToInvite],
        type:         "voice"
      });

      await createPeerForContact(email);
    }

    setParticipants(prev => Array.from(new Set([...prev, ...emailsToInvite])));
    setSelectedContactsToInvite([]);
    setShowInviteModal(false);
  }, [localUser, remoteUser, createPeerForContact]);

  // ── Listen for conference participant join/leave ───────────────────────────
  useEffect(() => {
    const socket = connectSocket();

    const handleJoin = ({ from }) => {
      setParticipants(prev => prev.includes(from) ? prev : [...prev, from]);
    };
    const handleLeave = ({ from }) => {
      setParticipants(prev => prev.filter(p => p !== from));
      // Close and remove that peer connection
      if (peersRef.current[from]) {
        try { peersRef.current[from].close(); } catch {}
        delete peersRef.current[from];
      }
    };

    socket.on("conference_participant_joined", handleJoin);
    socket.on("conference_participant_left",   handleLeave);
    return () => {
      socket.off("conference_participant_joined", handleJoin);
      socket.off("conference_participant_left",   handleLeave);
    };
  }, []);

  // ── Route incoming ICE candidates to the correct peer ─────────────────────
  // (conference peers only — main peer ICE is handled inside startCall)
  useEffect(() => {
    const socket = connectSocket();

    const handleConferenceICE = async ({ from, candidate }) => {
      const peer = peersRef.current[from];
      if (!peer) return;
      try {
        await peer.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (err) {
        console.error("Conference ICE error:", err);
      }
    };

    socket.on("call_ice", handleConferenceICE);
    return () => socket.off("call_ice", handleConferenceICE);
  }, []);

  // ── Handle call_end from remote user ─────────────────────────────────────
  useEffect(() => {
    const socket = connectSocket();
    const handleRemoteEnd = ({ from }) => {
      if (from?.toLowerCase() === remoteUser?.toLowerCase()) {
        endCallOnceRef.current?.();
      }
    };
    socket.on("call_end", handleRemoteEnd);
    return () => socket.off("call_end", handleRemoteEnd);
  }, [remoteUser]);

  // ── Autoplay workaround: resume audio on any click ────────────────────────
  useEffect(() => {
    const resume = () => {
      const audio = remoteAudioRef.current;
      if (audio && audio.paused && audio.srcObject) {
        audio.play().catch(() => {});
      }
    };
    document.addEventListener("click", resume);
    return () => document.removeEventListener("click", resume);
  }, []);

  // ── Main WebRTC setup ─────────────────────────────────────────────────────
  useEffect(() => {
    const signaling    = createVoiceCallSignaling(localUser, remoteUser);
    const signalingRef = { current: signaling };

    async function startCall() {
      pendingCandidates.current = [];
      isEndedRef.current        = false;

      try {
        const localStream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
          video: false
        });
        localStreamRef.current = localStream;

        // const pc = new RTCPeerConnection({
        //   iceServers: [
        //     { urls: "stun:stun.cloudflare.com:3478" },
        //     {
        //       urls:       process.env.REACT_APP_TURN_URL,
        //       username:   process.env.REACT_APP_TURN_USERNAME,
        //       credential: process.env.REACT_APP_TURN_CREDENTIAL,
        //     }
        //   ]
        // });
                    var pc = new RTCPeerConnection({
              iceServers: [
                  {
                    urls: "stun:stun.relay.metered.ca:80",
                  },
                  {
                    urls: "turn:global.relay.metered.ca:80",
                    username: "a4e8f95f2328fa27921e2991",
                    credential: "lT4ZU1kAeW1Vn6Nt",
                  },
                  {
                    urls: "turn:global.relay.metered.ca:80?transport=tcp",
                    username: "a4e8f95f2328fa27921e2991",
                    credential: "lT4ZU1kAeW1Vn6Nt",
                  },
                  {
                    urls: "turn:global.relay.metered.ca:443",
                    username: "a4e8f95f2328fa27921e2991",
                    credential: "lT4ZU1kAeW1Vn6Nt",
                  },
                  {
                    urls: "turns:global.relay.metered.ca:443?transport=tcp",
                    username: "a4e8f95f2328fa27921e2991",
                    credential: "lT4ZU1kAeW1Vn6Nt",
                  },
              ],
            });
                      
        // const pc = new RTCPeerConnection({
        //   iceServers: [
        //     { urls: "stun:stun.cloudflare.com:3478" },
        //     {
        //       urls: "turn:openrelay.metered.ca:80",
        //       username: "openrelayproject",
        //       credential: "openrelayproject"
        //     },
        //     {
        //       urls: "turn:openrelay.metered.ca:443",
        //       username: "openrelayproject",
        //       credential: "openrelayproject"
        //     }
        //   ]
        // });
        pcRef.current = pc;

        localStream.getTracks().forEach(t => pc.addTrack(t, localStream));

        // Single clean ontrack handler (fixed nested bug)
        pc.ontrack = (event) => {
          const remoteStream = new MediaStream();
          remoteStream.addTrack(event.track);
          attachRemoteStream(remoteStream);
        };

        pc.onicecandidate = (e) => {
          if (e.candidate) signaling.sendICE(e.candidate);
        };

        signaling.onICE = async (candidate) => {
          try {
            if (pc.remoteDescription) {
              await pc.addIceCandidate(new RTCIceCandidate(candidate));
            } else {
              pendingCandidates.current.push(candidate);
            }
          } catch (err) { console.error("ICE error:", err); }
        };

        pc.onconnectionstatechange = () => {
          const s = pc.connectionState;
          console.log("Connection state:", s);
          if (s === "failed") endCallOnceRef.current?.();
          if (s === "disconnected") console.warn("Temporary network issue");
        };

        pc.oniceconnectionstatechange = () => {
          const s = pc.iceConnectionState;
          console.log("ICE state:", s);
          if ((s === "connected" || s === "completed") && !timerRef.current) {
            timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
          } else if (s === "failed") {
            endCallOnceRef.current?.();
          }
        };

        signaling.onEnd = () => endCallOnceRef.current?.();

        const flushPendingCandidates = async () => {
          const candidates = pendingCandidates.current.splice(0);
          for (const c of candidates) {
            try { await pc.addIceCandidate(new RTCIceCandidate(c)); }
            catch (err) { console.error("Pending ICE error:", err); }
          }
        };

        if (isCaller) {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          signaling.sendOffer(offer);
          signaling.onAnswer = async (answer) => {
            try {
              await pc.setRemoteDescription(new RTCSessionDescription(answer));
              await flushPendingCandidates();
            } catch (err) { console.error("setRemoteDescription error:", err); }
          };
        } else if (incomingOffer) {
          await pc.setRemoteDescription(new RTCSessionDescription(incomingOffer));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          signaling.sendAnswer(answer);
          await flushPendingCandidates();
        }

      } catch (err) {
        console.error("startCall error:", err);
        setError("Call failed: " + err.message);
      }
    }

    startCall();

    // ICE restart on network restore
    const handleOnline = async () => {
      const pc = pcRef.current;
      if (!pc) return;
      try {
        const offer = await pc.createOffer({ iceRestart: true });
        await pc.setLocalDescription(offer);
        signaling.sendOffer(offer);
      } catch (err) { console.error("ICE Restart Error:", err); }
    };

    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("online", handleOnline);
      if (signalingRef.current?.cleanup) signalingRef.current.cleanup();
      endCallOnceRef.current?.();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localUser, remoteUser]);

  const remoteContact = contacts.find((contact) => {
    const email = contact?.emails?.[0] || contact?.email || contact?.id || contact?.contactId;
    return email && String(email).toLowerCase() === String(remoteUser).toLowerCase();
  });

  const remoteAvatarSrc = remoteContact?.profileImageUrl
    ? /^https?:\/\//i.test(remoteContact.profileImageUrl)
      ? remoteContact.profileImageUrl
      : `${SERVER_URL}${remoteContact.profileImageUrl}`
    : null;

  function formatTime(sec) {
    return `${String(Math.floor(sec / 60)).padStart(2, "0")}:${String(sec % 60).padStart(2, "0")}`;
  }

  return (
    <div className="call-screen">
      <audio
        ref={remoteAudioRef}
        autoPlay={true}
        playsInline={true}
        muted={false}
        controls={false}
        style={{ display: "none" }}
      />

      <div className="call-avatar">
        {remoteAvatarSrc ? (
          <img src={remoteAvatarSrc} alt={remoteUser || "contact"} className="call-avatar-image" />
        ) : (
          remoteUser?.charAt(0).toUpperCase()
        )}
      </div>
      <div className="call-name">{remoteUser}</div>
      <div className="call-status">{error || formatTime(duration)}</div>

      {/* Conference Participants Display */}
      {participants.length > 1 && (
        <div style={{
          position: "absolute", top: 16, right: 16,
          background: "rgba(15,23,42,0.9)",
          border: "1px solid rgba(59,130,246,0.3)",
          borderRadius: 12, padding: "12px 16px",
          maxWidth: 200, maxHeight: 150, overflowY: "auto",
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#3b82f6", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Conference Call ({participants.length})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {participants.map((p, i) => (
              <div key={i} style={{
                fontSize: 11, color: "#e2e8f0",
                padding: "4px 8px", background: "rgba(59,130,246,0.1)",
                borderRadius: 6, display: "flex", alignItems: "center", gap: 6
              }}>
                <span style={{ fontSize: 10, color: "#22c55e" }}>●</span>
                {p.split("@")[0] || p}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="call-controls-footer">
        <button className={`call-btn ${isMuted ? "muted" : ""}`} onClick={toggleMute} title="Toggle Microphone">
          <i className={`fa-solid ${isMuted ? "fa-microphone-slash" : "fa-microphone"}`} />
        </button>

        <button
          className="call-btn"
          onClick={() => setShowInviteModal(true)}
          title="Add Participant"
          style={{ background: "rgba(34,197,94,0.2)", color: "#22c55e" }}
        >
          <i className="fa-solid fa-user-plus" />
        </button>

        <button
          className="call-btn btn-end"
          onClick={() => {
            const socket = connectSocket();
            socket.emit("call_end", { to: remoteUser });
            participants.forEach(p => {
              if (p !== localUser && p !== remoteUser) {
                socket.emit("conference_end", { to: p });
              }
            });
            endCallOnce();
          }}
          title="End Call"
        >
          <i className="fa-solid fa-phone-slash" />
        </button>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
          onClick={() => setShowInviteModal(false)}
        >
          <div
            style={{ background: "#0f172a", border: "1px solid rgba(59,130,246,0.3)", borderRadius: 16, padding: 24, maxWidth: 420, width: "90%", maxHeight: "80vh", overflow: "auto" }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#f1f5f9" }}>Add to Conference</h3>
              <button onClick={() => setShowInviteModal(false)} style={{ background: "none", border: "none", color: "#64748b", fontSize: 24, cursor: "pointer", padding: 0, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
            </div>

            <div style={{ maxHeight: 300, overflowY: "auto", marginBottom: 16 }}>
              {contacts.filter(c => {
                const email = c.emails?.[0] || c.email;
                return email && !participants.includes(email);
              }).length === 0 ? (
                <div style={{ textAlign: "center", color: "#64748b", padding: "24px 0", fontSize: 13 }}>
                  No more contacts to add
                </div>
              ) : (
                contacts.filter(c => {
                  const email = c.emails?.[0] || c.email;
                  return email && !participants.includes(email);
                }).map(contact => {
                  const email      = contact.emails?.[0] || contact.email;
                  const isSelected = selectedContactsToInvite.includes(email);
                  return (
                    <div
                      key={email}
                      onClick={() => setSelectedContactsToInvite(prev =>
                        isSelected ? prev.filter(e => e !== email) : [...prev, email]
                      )}
                      style={{
                        display: "flex", alignItems: "center", gap: 12,
                        padding: "12px", marginBottom: 6, borderRadius: 10,
                        background: isSelected ? "rgba(59,130,246,0.2)" : "rgba(59,130,246,0.05)",
                        border: `1px solid ${isSelected ? "rgba(59,130,246,0.5)" : "rgba(59,130,246,0.2)"}`,
                        cursor: "pointer", transition: "all 0.15s",
                      }}
                    >
                      <input type="checkbox" checked={isSelected} onChange={() => {}} className="chkcss" style={{ cursor: "pointer", marginRight: 4 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#f1f5f9", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {contact.name || email.split("@")[0]}
                        </div>
                        <div style={{ fontSize: 11, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {email}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowInviteModal(false)}
                style={{ padding: "10px 16px", background: "rgba(148,163,184,0.12)", border: "1px solid rgba(148,163,184,0.3)", borderRadius: 8, color: "#cbd5e1", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
              >
                Cancel
              </button>
              <button
                onClick={() => { if (selectedContactsToInvite.length > 0) inviteToCall(selectedContactsToInvite); }}
                disabled={selectedContactsToInvite.length === 0}
                style={{
                  padding: "10px 16px",
                  background: selectedContactsToInvite.length > 0 ? "rgba(34,197,94,0.2)" : "rgba(34,197,94,0.1)",
                  border: "1px solid rgba(34,197,94,0.3)", borderRadius: 8,
                  color: selectedContactsToInvite.length > 0 ? "#22c55e" : "#475569",
                  cursor: selectedContactsToInvite.length > 0 ? "pointer" : "not-allowed",
                  fontSize: 13, fontWeight: 600
                }}
              >
                Invite {selectedContactsToInvite.length > 0 ? `(${selectedContactsToInvite.length})` : ""}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
